import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  HiOutlineViewList,
  HiOutlineMap,
  HiOutlineFilter,
  HiOutlineSearch,
  HiOutlineOfficeBuilding,
  HiHeart,
  HiOutlineHeart,
} from 'react-icons/hi';
import { Warehouse, WarehouseMatchList } from '@views/WarehouseDetails/types';
import {
  WAREHOUSE_OPERATIONS,
  ADDITIONAL_SERVICES,
} from '@views/WarehouseDetails/constants';
import { FloxSpinner } from '@tailwindComponents/FloxSpinner';
import { useMediaQuery } from 'usehooks-ts';
import { useEffect, useState, useRef, useCallback } from 'react';
import {
  ADD_WAREHOUSE_MATCH,
  DELETE_WAREHOUSE_MATCH,
  MATCH_LIST,
  WAREHOUSE_SEARCH_URL,
  WAREHOUSE_IMAGES_URL,
  SUPPORTED_CURRENCY_GROUPS,
} from '../../constants';
import { useAuthHttp } from '../../hooks';
import { useAtom } from 'jotai';
import { warehouseSearchMapViewAtom } from '../../store/jotai';
import { FiltersSideDrawer } from './FiltersSideDrawer';
import { Mapbox } from './Mapbox';
import { isModifiedClick } from './modifiedClick';
import {
  clearWarehouseSearchFilters,
  createEmptyWarehouseSearchFilters,
  loadWarehouseSearchFilters,
  saveWarehouseSearchFilters,
} from './searchFiltersStorage';
import { PageHeader } from '../../tailwindComponents/PageHeader';
import { Modal as TailwindModal } from '../../tailwindComponents/Modal/Modal';

const SYSTEMS = ['WMS', 'TMS', 'WiFi', 'API', 'EDI', 'Other'];

const SAFETY_AND_SECURITY = [
  '24/7 Security',
  '24/7 Access',
  'Bunded',
  'Caged',
  'Sprinklered',
  'Alarmed',
  'Patrolled',
  'Fenced Yard',
  'CCTV',
  'ESFR Sprinkler System',
];

export type WarehouseWithMatchStatus = Warehouse & {
  matchStatus: string;
  matchId?: string;
};

type WarehouseMatchDetails = {
  matchStatus: string;
  matchId?: string;
};

const REMOVABLE_MATCH_STATUSES = ['Not engaged', 'Negotiating', 'Quarantined'];

export function WarehouseSearch() {
  const isDesktop = useMediaQuery('(min-width: 62em)');
  const authHttp = useAuthHttp();
  const navigate = useNavigate();
  const [restoredFilters] = useState(loadWarehouseSearchFilters);
  const [savedMapView, setSavedMapView] = useAtom(warehouseSearchMapViewAtom);
  const [searchParams, setSearchParams] = useSearchParams();
  const [location, setLocation] = useState<{
    lat: number | null;
    lon: number | null;
  }>({
    lat: null,
    lon: null,
  });
  const [inputValue, setInputValue] = useState(restoredFilters.inputValue);
  const [animatedPlaceholder, setAnimatedPlaceholder] = useState(
    'Search warehouses: try '
  );
  const inputRef = useRef<HTMLInputElement>(null);
  const [mapPosition, setMapPosition] = useState<{
    lat: number;
    lon: number;
  } | null>(savedMapView?.center ?? { lat: 54.3781, lon: -2.436 });
  const [zoomLevel, setZoomLevel] = useState(savedMapView?.zoom ?? 5);
  const hasRestoredFromAtom = useRef(
    !!savedMapView && !searchParams.get('lat') && !searchParams.get('lng')
  );
  const [warehouses, setWarehouses] = useState<WarehouseWithMatchStatus[]>([]);
  const [liveMatches, setLiveMatches] = useState<WarehouseWithMatchStatus[]>(
    []
  );
  const [isFetchingWarehouses, setIsFetchingWarehouses] = useState(false);
  const [hasFetchedWarehouses, setHasFetchedWarehouses] = useState(false);
  const [isFetchingMatches, setIsFetchingMatches] = useState(false);
  const [matchOverrides, setMatchOverrides] = useState<
    Record<string, WarehouseMatchDetails>
  >({});
  const [pendingShortlistWarehouseId, setPendingShortlistWarehouseId] =
    useState('');
  const [currency, setCurrency] = useState(restoredFilters.currency);
  const [specialCapabilities, setSpecialCapabilities] = useState<string[]>(
    restoredFilters.specialCapabilities
  );
  const [valueAddingServices, setValueAddingServices] = useState<string[]>(
    restoredFilters.valueAddingServices
  );
  const [systemsAndSecurity, setSystemsAndSecurity] = useState<string[]>(
    restoredFilters.systemsAndSecurity
  );
  const [temperatureControls, setTemperatureControls] = useState<string[]>(
    restoredFilters.temperatureControls
  );
  const [sectorExperience, setSectorExperience] = useState<string[]>(
    restoredFilters.sectorExperience
  );
  const [fulfilmentServices, setFulfilmentServices] = useState<string[]>(
    restoredFilters.fulfilmentServices
  );
  const [materialHandlingEquipment, setMaterialHandlingEquipment] = useState<
    string[]
  >(restoredFilters.materialHandlingEquipment);
  const [accreditationsAndMemberships, setAccreditationsAndMemberships] =
    useState<string[]>(restoredFilters.accreditationsAndMemberships);
  const [storageCapacityUnitOfMeasure, setStorageCapacityUnitOfMeasure] =
    useState(restoredFilters.storageCapacityUnitOfMeasure);
  const [minAvailableStorageCapacity, setMinAvailableStorageCapacity] =
    useState(restoredFilters.minAvailableStorageCapacity);
  const [requiresRacking, setRequiresRacking] = useState(
    restoredFilters.requiresRacking
  );
  const [minRackingHeight, setMinRackingHeight] = useState(
    restoredFilters.minRackingHeight
  );
  const [minRackingWidth, setMinRackingWidth] = useState(
    restoredFilters.minRackingWidth
  );
  const [minRackingDepth, setMinRackingDepth] = useState(
    restoredFilters.minRackingDepth
  );
  const [fromPrice, setFromPrice] = useState(restoredFilters.fromPrice);
  const [toPrice, setToPrice] = useState(restoredFilters.toPrice);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'map' | 'list'>(
    restoredFilters.viewMode
  );
  const [isAiSearching, setIsAiSearching] = useState(false);
  const [showNoResultsModal, setShowNoResultsModal] = useState(false);
  const isAiSearchRef = useRef(false);

  useEffect(() => {
    if (inputValue.length > 0) return;

    const prefix = 'Search warehouses: try ';
    const phrases = [
      'need 2500 pallet cold storage in Leeds',
      'short term overflow storage 3000 pallets near Nottingham',
      'need pick and pack fulfilment for ecommerce near Houston, TX',
      'bonded warehouse in Liverpool for 70 pallets',
      'pallet storage around Lyon, about 800 pallets',
    ];

    let phraseIdx = 0;
    let charIdx = 0;
    let deleting = false;
    let timeoutId: ReturnType<typeof setTimeout>;

    const tick = () => {
      const phrase = phrases[phraseIdx];
      if (!deleting) {
        charIdx++;
        setAnimatedPlaceholder(prefix + phrase.slice(0, charIdx));
        if (charIdx === phrase.length) {
          deleting = true;
          timeoutId = setTimeout(tick, 1800);
          return;
        }
        timeoutId = setTimeout(tick, 45 + Math.random() * 45);
      } else {
        charIdx--;
        setAnimatedPlaceholder(prefix + phrase.slice(0, charIdx));
        if (charIdx === 0) {
          deleting = false;
          phraseIdx = (phraseIdx + 1) % phrases.length;
          timeoutId = setTimeout(tick, 400);
          return;
        }
        timeoutId = setTimeout(tick, 25);
      }
    };

    timeoutId = setTimeout(tick, 600);
    return () => clearTimeout(timeoutId);
  }, [inputValue]);

  const locationParam = searchParams.get('location') || '';
  const latParam = searchParams.get('lat') || null;
  const lngParam = searchParams.get('lng') || null;

  const handleFetchWarehouseData = async (
    _location?: {
      lat: number;
      lon: number;
    },
    radius = 250
  ) => {
    setIsFetchingWarehouses(true);
    const warehouseOps = valueAddingServices.filter((service) =>
      WAREHOUSE_OPERATIONS.includes(service)
    );
    const addServices = valueAddingServices.filter((service) =>
      ADDITIONAL_SERVICES.includes(service)
    );
    const sysAvail = systemsAndSecurity.filter((system) =>
      SYSTEMS.includes(system)
    );
    const safSec = systemsAndSecurity.filter((system) =>
      SAFETY_AND_SECURITY.includes(system)
    );
    try {
      const { data: fetchedWarehouseData } = await authHttp.post(
        WAREHOUSE_SEARCH_URL,
        {
          location:
            _location ??
            (location.lat && location.lon ? location : undefined) ??
            (mapPosition && mapPosition.lat && mapPosition.lon
              ? mapPosition
              : undefined),
          distanceUnit: 'km',
          radius,
          pagination: {
            pageNumber: 0,
            itemsPerPage: 5000,
          },
          warehouseOperations:
            warehouseOps.length > 0 ? warehouseOps : undefined,
          additionalServices:
            addServices.length > 0 ? addServices : undefined,
          storageCapabilities:
            specialCapabilities.length > 0 ? specialCapabilities : undefined,
          systemsAvailable: sysAvail.length > 0 ? sysAvail : undefined,
          safetyAndSecurity: safSec.length > 0 ? safSec : undefined,
          currency: currency || undefined,
          minSquareFeetStoragePricePerWeek: fromPrice
            ? Number(fromPrice)
            : null,
          maxSquareFeetStoragePricePerWeek: toPrice ? Number(toPrice) : null,
          temperatureControlTypes:
            temperatureControls?.length > 0 ? temperatureControls : undefined,
          sectorExperience:
            sectorExperience?.length > 0 ? sectorExperience : undefined,
          fulfilmentServices:
            fulfilmentServices?.length > 0 ? fulfilmentServices : undefined,
          materialHandlingEquipment:
            materialHandlingEquipment?.length > 0
              ? materialHandlingEquipment
              : undefined,
          accreditationsAndMemberships:
            accreditationsAndMemberships?.length > 0
              ? accreditationsAndMemberships
              : undefined,
          storageCapacityUnitOfMeasure:
            storageCapacityUnitOfMeasure &&
            !!Number(minAvailableStorageCapacity)
              ? storageCapacityUnitOfMeasure
              : undefined,
          minAvailableStorageCapacity:
            !!storageCapacityUnitOfMeasure &&
            !!Number(minAvailableStorageCapacity)
              ? Number(minAvailableStorageCapacity)
              : undefined,
          isRackingAvailable: requiresRacking ? true : undefined,
          minIndividualRackHeight:
            requiresRacking && minRackingHeight
              ? Number(minRackingHeight)
              : undefined,
          minIndividualRackWidth:
            requiresRacking && minRackingWidth
              ? Number(minRackingWidth)
              : undefined,
          minIndividualRackDepth:
            requiresRacking && minRackingDepth
              ? Number(minRackingDepth)
              : undefined,
        }
      );

      if (fetchedWarehouseData) setWarehouses(fetchedWarehouseData.warehouses);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('error: ', error);
    } finally {
      setIsFetchingWarehouses(false);
      setHasFetchedWarehouses(true);
    }
  };

  const fetchWarehouseShortlist = async () => {
    setIsFetchingMatches(true);
    try {
      const { data } = await authHttp.post<WarehouseMatchList>(
        MATCH_LIST,
        {},
        { headers: { 'Content-Type': 'application/json' } }
      );

      if (data.matches.length > 0) {
        const matches = data.matches.filter(
          (match) => match.match.matchStatus !== 'Rejected'
        );
        setLiveMatches(
          matches.map((match) => ({
            ...match.warehouse,
            matchStatus: match.match.matchStatus,
            matchId: match.match.id,
          }))
        );
        setMatchOverrides({});
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      setIsFetchingMatches(false);
    }
  };

  useEffect(() => {
    if (warehouses.length <= 0) return;
    if (isAiSearchRef.current) {
      isAiSearchRef.current = false;
      return;
    }
    fetchWarehouseShortlist();
  }, [warehouses]);

  const sortedWarehouses = [
    ...liveMatches.filter((lm) =>
      warehouses.some((w) => w.warehouseId === lm.warehouseId)
    ),
    ...warehouses.filter(
      (w) => !liveMatches.some((lm) => lm.warehouseId === w.warehouseId)
    ),
  ];

  const getMatchDetails = (
    warehouse: WarehouseWithMatchStatus
  ): WarehouseMatchDetails =>
    matchOverrides[warehouse.warehouseId] ?? {
      matchStatus: warehouse.matchStatus,
      matchId: warehouse.matchId,
    };

  const shortlistedWarehouseIds = new Set(
    liveMatches.map((lm) => lm.warehouseId)
  );
  Object.entries(matchOverrides).forEach(([warehouseId, override]) => {
    if (override.matchStatus) shortlistedWarehouseIds.add(warehouseId);
    else shortlistedWarehouseIds.delete(warehouseId);
  });

  useEffect(() => {
    if (warehouses.length > 0) return;
    if (isAiSearchRef.current) return;
    // Guard the fallback fetch so it only runs as a one-shot mount initializer.
    // Without this, a Filter request that returns zero results sets warehouses
    // to a fresh empty array, re-triggering this effect, which re-fires the
    // fetch with the same filter state, looping forever.
    if (hasFetchedWarehouses) return;
    handleFetchWarehouseData({ lat: 54.3781, lon: -2.436 }, 150000);
  }, [warehouses, hasFetchedWarehouses]);

  useEffect(() => {
    if (!latParam || !lngParam) return;
    // Skip URL param restoration if we just restored from saved viewport
    if (hasRestoredFromAtom.current) {
      hasRestoredFromAtom.current = false;
      return;
    }
    setMapPosition({ lat: Number(latParam), lon: Number(lngParam) });
    setZoomLevel(10);
    handleFetchWarehouseData({ lat: Number(latParam), lon: Number(lngParam) });
  }, [latParam, lngParam]);

  useEffect(() => {
    if (!locationParam) return;
    setInputValue(locationParam);
  }, [locationParam]);

  useEffect(() => {
    if (hasFetchedWarehouses) return;
    fetchWarehouseShortlist();
  }, [hasFetchedWarehouses]);

  useEffect(() => {
    saveWarehouseSearchFilters({
      inputValue,
      viewMode,
      currency,
      specialCapabilities,
      valueAddingServices,
      systemsAndSecurity,
      temperatureControls,
      sectorExperience,
      fulfilmentServices,
      materialHandlingEquipment,
      accreditationsAndMemberships,
      storageCapacityUnitOfMeasure,
      minAvailableStorageCapacity,
      requiresRacking,
      minRackingHeight,
      minRackingWidth,
      minRackingDepth,
      fromPrice,
      toPrice,
    });
  }, [
    inputValue,
    viewMode,
    currency,
    specialCapabilities,
    valueAddingServices,
    systemsAndSecurity,
    temperatureControls,
    sectorExperience,
    fulfilmentServices,
    materialHandlingEquipment,
    accreditationsAndMemberships,
    storageCapacityUnitOfMeasure,
    minAvailableStorageCapacity,
    requiresRacking,
    minRackingHeight,
    minRackingWidth,
    minRackingDepth,
    fromPrice,
    toPrice,
  ]);

  const handleViewportChange = useCallback(
    (center: { lat: number; lon: number }, zoom: number) => {
      setSavedMapView({ center, zoom });
    },
    [setSavedMapView]
  );

  const handleFilterClick = () => {
    setIsFiltersOpen(false);
    handleFetchWarehouseData();
  };

  const handleClearFilters = () => {
    const empty = createEmptyWarehouseSearchFilters();
    setCurrency(empty.currency);
    setSpecialCapabilities(empty.specialCapabilities);
    setValueAddingServices(empty.valueAddingServices);
    setSystemsAndSecurity(empty.systemsAndSecurity);
    setTemperatureControls(empty.temperatureControls);
    setSectorExperience(empty.sectorExperience);
    setFulfilmentServices(empty.fulfilmentServices);
    setMaterialHandlingEquipment(empty.materialHandlingEquipment);
    setAccreditationsAndMemberships(empty.accreditationsAndMemberships);
    setStorageCapacityUnitOfMeasure(empty.storageCapacityUnitOfMeasure);
    setMinAvailableStorageCapacity(empty.minAvailableStorageCapacity);
    setRequiresRacking(empty.requiresRacking);
    setMinRackingHeight(empty.minRackingHeight);
    setMinRackingWidth(empty.minRackingWidth);
    setMinRackingDepth(empty.minRackingDepth);
    setFromPrice(empty.fromPrice);
    setToPrice(empty.toPrice);
    clearWarehouseSearchFilters();
  };

  const handleToggleShortlist = async (
    warehouseId: string,
    match: WarehouseMatchDetails
  ) => {
    if (pendingShortlistWarehouseId) return;
    setPendingShortlistWarehouseId(warehouseId);

    try {
      if (!match.matchStatus) {
        const { data } = await authHttp.post(
          ADD_WAREHOUSE_MATCH,
          { match: { warehouseId } },
          { headers: { 'Content-Type': 'application/json' } }
        );

        if (data?.match) {
          setMatchOverrides((current) => ({
            ...current,
            [warehouseId]: {
              matchStatus: data.match.matchStatus,
              matchId: data.match.id,
            },
          }));
        }
        return;
      }

      if (
        !match.matchId ||
        !REMOVABLE_MATCH_STATUSES.includes(match.matchStatus)
      ) {
        return;
      }

      const { data } = await authHttp.post(
        DELETE_WAREHOUSE_MATCH,
        { match: { matchId: match.matchId } },
        { headers: { 'Content-Type': 'application/json' } }
      );

      if (data?.match) {
        setMatchOverrides((current) => ({
          ...current,
          [warehouseId]: { matchStatus: '' },
        }));
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error updating shortlist:', error);
    } finally {
      setPendingShortlistWarehouseId('');
    }
  };

  const handleAiSearch = async () => {
    const query = inputValue.trim();
    if (!query) return;

    setIsAiSearching(true);
    isAiSearchRef.current = true;

    try {
      const { data } = await authHttp.post(
        '/mcp/copilot/warehouse-search',
        { query },
        { baseURL: '/' }
      );

      if (data.warehouses && data.warehouses.length > 0) {
        setWarehouses(data.warehouses);

        // Center map on first warehouse if it has coordinates
        const firstWithCoords = data.warehouses.find(
          (w: any) => w.location?.lat && w.location?.lon
        );
        if (firstWithCoords) {
          setMapPosition({
            lat: firstWithCoords.location.lat,
            lon: firstWithCoords.location.lon,
          });
          setZoomLevel(8);
        }
      } else {
        setShowNoResultsModal(true);
      }
    } catch (error: any) {
      console.error('AI search error:', error);
      isAiSearchRef.current = false;
      handleFetchWarehouseData();
    } finally {
      setIsAiSearching(false);
    }
  };

  const pages = [
    { name: 'Home', href: '/', current: false },
    { name: 'Warehouse Search', href: '/warehouse-search', current: true },
  ];

  if (isFetchingWarehouses || isFetchingMatches) {
    return (
      <>
        <PageHeader header='Warehouse Search' pages={pages} />
        <main className='flex flex-col gap-6 p-4 lg:px-8 lg:pb-8'>
          <div className='bg-white rounded-lg shadow-sm border border-gray-200 p-12'>
            <div className='flex flex-col items-center gap-3'>
              <FloxSpinner size='md' />
              <p className='text-gray-500 text-sm'>Loading warehouses...</p>
            </div>
          </div>
        </main>
      </>
    );
  }

  return (
    <>
      <PageHeader header='Warehouse Search' pages={pages} />
      <main className='flex flex-col gap-6 p-4 lg:px-8 lg:pb-8'>
        {/* Search, Filters & View Toggle */}
        <div className='flex items-center gap-3'>
          {/* AI Search Input */}
          <div className='relative flex-1'>
            <FloxSpinner
              size='sm'
              isAnimating={isAiSearching}
              className='absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5'
            />
            <input
              ref={inputRef}
              type='text'
              placeholder={animatedPlaceholder}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleAiSearch();
                }
              }}
              disabled={isAiSearching}
              className='w-full pl-10 pr-28 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 disabled:opacity-50'
            />
            <button
              type='button'
              onClick={handleAiSearch}
              disabled={isAiSearching || !inputValue.trim()}
              className='absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
            >
              {isAiSearching ? (
                <svg className='animate-spin h-4 w-4' viewBox='0 0 24 24'>
                  <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4' fill='none' />
                  <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z' />
                </svg>
              ) : (
                <HiOutlineSearch className='w-4 h-4' />
              )}
              {isAiSearching ? 'Searching...' : 'Search'}
            </button>
          </div>

          {/* Filters Button */}
          <button
            type='button'
            onClick={() => setIsFiltersOpen(true)}
            className='inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors flex-shrink-0'
          >
            <HiOutlineFilter className='w-4 h-4' />
            Filters
          </button>

          {/* Shortlist Button */}
          <a
            href='/warehouse-shortlist'
            onClick={(e) => {
              if (isModifiedClick(e)) return;
              e.preventDefault();
              navigate('/warehouse-shortlist');
            }}
            className='inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-primary bg-white border border-primary rounded-lg hover:bg-blue-50 transition-colors flex-shrink-0'
          >
            <HiHeart className='w-4 h-4 text-red-500' />
            Shortlist ({shortlistedWarehouseIds.size})
          </a>

          {/* View Toggle */}
          <div className='relative flex items-center h-10 bg-gray-100 rounded-full shadow-sm px-1 flex-shrink-0'>
            <div
              className='absolute top-1 left-1 w-10 h-8 rounded-full transition-transform duration-200 bg-blue-600 z-0'
              style={{
                transition: 'transform 0.2s',
                transform:
                  viewMode === 'list' ? 'translateX(41px)' : 'translateX(0)',
              }}
            />
            <button
              type='button'
              className={`relative z-10 flex items-center justify-center w-10 h-8 rounded-full transition-colors duration-200 ${
                viewMode === 'map' ? 'text-white' : 'text-blue-600'
              }`}
              onClick={() => setViewMode('map')}
              aria-label='Map view'
            >
              <HiOutlineMap size={20} />
            </button>
            <button
              type='button'
              className={`relative z-10 flex items-center justify-center w-10 h-8 rounded-full transition-colors duration-200 ${
                viewMode === 'list' ? 'text-white' : 'text-blue-600'
              }`}
              onClick={() => setViewMode('list')}
              aria-label='List view'
            >
              <HiOutlineViewList size={20} />
            </button>
          </div>
        </div>

        {/* Map or List View */}
        {viewMode === 'map' ? (
          <Mapbox
            warehouses={warehouses}
            mapPosition={
              mapPosition ??
              (latParam !== null && lngParam !== null
                ? { lat: Number(latParam), lon: Number(lngParam) }
                : null)
            }
            zoomLevel={zoomLevel}
            onViewportChange={handleViewportChange}
          />
        ) : (
          <div className='bg-white rounded-lg border border-gray-200 overflow-hidden'>
            {/* Table */}
            <div className='overflow-x-auto'>
              <table className='w-full'>
                <thead>
                  <tr className='border-b border-gray-200 bg-gray-50'>
                    <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[280px]'>
                      Warehouse
                    </th>
                    <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      Capacity
                    </th>
                    <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      Rates
                    </th>
                    <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      Own Fleet
                    </th>
                    <th className='px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-24'>
                      Shortlisted
                    </th>
                    <th className='w-32 px-4 py-3'>
                      <span className='sr-only'>Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className='divide-y divide-gray-200'>
                  {sortedWarehouses.length === 0 && (
                    <tr>
                      <td colSpan={6} className='text-center py-12'>
                        <div className='flex flex-col items-center gap-3'>
                          <HiOutlineOfficeBuilding className='w-12 h-12 text-gray-300' />
                          <p className='text-gray-900 font-medium'>
                            No warehouses found
                          </p>
                          <p className='text-gray-500 text-sm'>
                            Try adjusting your search or filters
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}

                  {sortedWarehouses.map(
                    (warehouse: WarehouseWithMatchStatus) => {
                      const currencies = SUPPORTED_CURRENCY_GROUPS.flatMap(
                        (currencyGroup) => currencyGroup.currencies
                      );
                      const currencySymbol =
                        currencies.find((c) => c.code === warehouse.currency)
                          ?.symbol || '£';

                      // Fleet calculation
                      const fleet =
                        typeof warehouse.fleet === 'string'
                          ? JSON.parse(warehouse.fleet || '[]')
                          : warehouse.fleet;

                      const getFleetBreakdown = () => {
                        const breakdown: { label: string; count: number }[] =
                          [];
                        if (!Array.isArray(fleet) || fleet.length === 0)
                          return null;

                        fleet.forEach(
                          ({
                            type,
                            count,
                          }: {
                            type: string;
                            count: number;
                          }) => {
                            if (
                              type === 'MultiAxleArticMoreThan26t' ||
                              type === 'TwoAxleRigidLessThan26t' ||
                              type === 'ThreeAxleRigidLessThan26t'
                            ) {
                              const existing = breakdown.find(
                                (b) => b.label === 'Artics > 26t'
                              );
                              if (existing) {
                                existing.count += count;
                              } else {
                                breakdown.push({
                                  label: 'Artics > 26t',
                                  count,
                                });
                              }
                            } else if (type === 'LgvLessThan3AndHalft') {
                              const existing = breakdown.find(
                                (b) => b.label === 'LDVs < 3.5t'
                              );
                              if (existing) {
                                existing.count += count;
                              } else {
                                breakdown.push({ label: 'LDVs < 3.5t', count });
                              }
                            } else if (type === 'VanLessThan1AndHalft') {
                              const existing = breakdown.find(
                                (b) => b.label === 'Van < 1.5t'
                              );
                              if (existing) {
                                existing.count += count;
                              } else {
                                breakdown.push({ label: 'Van < 1.5t', count });
                              }
                            } else if (
                              type === 'TwoAxleRigid18t' ||
                              type === 'TwoAxleRigidLessThan7AndHalf5t'
                            ) {
                              const existing = breakdown.find(
                                (b) => b.label === 'Rigids'
                              );
                              if (existing) {
                                existing.count += count;
                              } else {
                                breakdown.push({ label: 'Rigids', count });
                              }
                            }
                          }
                        );

                        return breakdown;
                      };

                      const fleetBreakdown = getFleetBreakdown();

                      const match = getMatchDetails(warehouse);
                      const isShortlisted = Boolean(match.matchStatus);
                      const isShortlistLocked =
                        isShortlisted &&
                        !REMOVABLE_MATCH_STATUSES.includes(match.matchStatus);
                      const isShortlistPending =
                        pendingShortlistWarehouseId === warehouse.warehouseId;
                      const warehouseLabel =
                        warehouse.name || `warehouse in ${warehouse.city}`;

                      return (
                        <tr
                          key={warehouse.warehouseId}
                          className='hover:bg-gray-50 cursor-pointer'
                          onClick={() =>
                            navigate(`/warehouse/${warehouse.warehouseId}`)
                          }
                        >
                          {/* Image + Name + Address */}
                          <td className='px-4 py-4'>
                            <div className='flex items-center gap-4'>
                              <div className='w-24 h-16 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0'>
                                {warehouse.images &&
                                warehouse.images.length > 0 ? (
                                  <img
                                    src={`${WAREHOUSE_IMAGES_URL}/${warehouse.images[0].imageLocation}/small/${warehouse.images[0].remoteName}`}
                                    alt={warehouse.name || warehouse.city}
                                    className='w-full h-full object-cover'
                                  />
                                ) : (
                                  <div className='w-full h-full flex items-center justify-center'>
                                    <HiOutlineOfficeBuilding className='w-8 h-8 text-gray-400' />
                                  </div>
                                )}
                              </div>
                              <div className='flex flex-col'>
                                <span className='text-sm font-medium text-gray-900'>
                                  {warehouse.name ||
                                    `Warehouse in ${warehouse.city}`}
                                </span>
                                <span className='text-sm text-gray-500 line-clamp-1'>
                                  {warehouse.address}
                                </span>
                              </div>
                            </div>
                          </td>

                          {/* Capacity */}
                          <td className='px-4 py-4'>
                            <div className='flex flex-col gap-1'>
                              <span className='text-sm font-medium text-gray-900'>
                                {warehouse.maxStorageCapacity}{' '}
                                {warehouse.storageCapacityUnitOfMeasure?.toLowerCase()}
                              </span>
                              <p className='text-xs text-gray-500'>
                                Max capacity
                              </p>
                            </div>
                          </td>

                          {/* Rates */}
                          <td className='px-4 py-4'>
                            <div className='flex flex-col gap-1'>
                              {warehouse.palletStoragePricePerWeek ? (
                                <>
                                  <span className='text-sm font-medium text-gray-900'>
                                    {currencySymbol}
                                    {warehouse.palletStoragePricePerWeek}
                                  </span>
                                  <p className='text-xs text-gray-500'>
                                    per pallet/week
                                  </p>
                                </>
                              ) : (
                                <span className='text-sm text-gray-500'>
                                  PoA
                                </span>
                              )}
                            </div>
                          </td>

                          {/* Own Fleet */}
                          <td className='px-4 py-4'>
                            {fleetBreakdown && fleetBreakdown.length > 0 ? (
                              <div className='flex flex-col gap-1'>
                                {fleetBreakdown.map((item) => (
                                  <div key={item.label} className='text-sm'>
                                    <span className='font-medium text-gray-900'>
                                      {item.count}
                                    </span>{' '}
                                    <span className='text-gray-500'>
                                      {item.label}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <span className='text-sm text-gray-500'>-</span>
                            )}
                          </td>

                          {/* Shortlisted */}
                          <td className='px-4 py-4 text-center'>
                            <button
                              type='button'
                              disabled={isShortlistLocked || isShortlistPending}
                              aria-pressed={isShortlisted}
                              aria-label={
                                isShortlisted
                                  ? `Remove ${warehouseLabel} from shortlist`
                                  : `Add ${warehouseLabel} to shortlist`
                              }
                              title={
                                isShortlistLocked
                                  ? `Manage this ${match.matchStatus.toLowerCase()} match from your shortlist`
                                  : undefined
                              }
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggleShortlist(
                                  warehouse.warehouseId,
                                  match
                                );
                              }}
                              className='p-1 rounded-full hover:bg-gray-100 disabled:hover:bg-transparent disabled:cursor-not-allowed transition-colors'
                            >
                              {isShortlisted ? (
                                <HiHeart className='w-5 h-5 text-red-500 mx-auto' />
                              ) : (
                                <HiOutlineHeart className='w-5 h-5 text-gray-400 mx-auto' />
                              )}
                            </button>
                          </td>

                          {/* Actions */}
                          <td className='px-4 py-4'>
                            <a
                              href={`/warehouse/${warehouse.warehouseId}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (isModifiedClick(e)) return;
                                e.preventDefault();
                                navigate(`/warehouse/${warehouse.warehouseId}`);
                              }}
                              className='inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors'
                            >
                              View Details
                            </a>
                          </td>
                        </tr>
                      );
                    }
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
      <FiltersSideDrawer
        isOpen={isFiltersOpen}
        setIsOpen={setIsFiltersOpen}
        isDesktop={isDesktop}
        currency={currency}
        setCurrency={setCurrency}
        specialCapabilities={specialCapabilities}
        setSpecialCapabilities={setSpecialCapabilities}
        valueAddingServices={valueAddingServices}
        setValueAddingServices={setValueAddingServices}
        systemsAndSecurity={systemsAndSecurity}
        setSystemsAndSecurity={setSystemsAndSecurity}
        temperatureControls={temperatureControls}
        setTemperatureControls={setTemperatureControls}
        sectorExperience={sectorExperience}
        setSectorExperience={setSectorExperience}
        fulfilmentServices={fulfilmentServices}
        setFulfilmentServices={setFulfilmentServices}
        materialHandlingEquipment={materialHandlingEquipment}
        setMaterialHandlingEquipment={setMaterialHandlingEquipment}
        accreditationsAndMemberships={accreditationsAndMemberships}
        setAccreditationsAndMemberships={setAccreditationsAndMemberships}
        storageCapacityUnitOfMeasure={storageCapacityUnitOfMeasure}
        setStorageCapacityUnitOfMeasure={setStorageCapacityUnitOfMeasure}
        minAvailableStorageCapacity={minAvailableStorageCapacity}
        setMinAvailableStorageCapacity={setMinAvailableStorageCapacity}
        requiresRacking={requiresRacking}
        setRequiresRacking={setRequiresRacking}
        minRackingHeight={minRackingHeight}
        setMinRackingHeight={setMinRackingHeight}
        minRackingWidth={minRackingWidth}
        setMinRackingWidth={setMinRackingWidth}
        minRackingDepth={minRackingDepth}
        setMinRackingDepth={setMinRackingDepth}
        fromPrice={fromPrice}
        setFromPrice={setFromPrice}
        toPrice={toPrice}
        setToPrice={setToPrice}
        handleFilterClick={handleFilterClick}
        handleClearFilters={handleClearFilters}
      />

      {/* No Results Modal */}
      <TailwindModal
        isOpen={showNoResultsModal}
        setIsOpen={setShowNoResultsModal}
        title='No warehouses found'
        className='sm:!w-96 sm:!min-w-0 sm:!max-w-md'
      >
        <div className='flex flex-col items-center gap-3 text-center'>
          <div className='w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center'>
            <HiOutlineOfficeBuilding className='w-6 h-6 text-gray-400' />
          </div>
          <p className='text-sm text-gray-500'>
            We couldn't find any warehouses matching your search. Try broadening your criteria or using different terms.
          </p>
          <button
            type='button'
            onClick={() => setShowNoResultsModal(false)}
            className='mt-2 w-full px-4 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors'
          >
            Got it
          </button>
        </div>
      </TailwindModal>
    </>
  );
}
